import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import {transformYupErrorsIntoObject} from 'utils/validators';

import {type RootState} from 'store/store';
import {getTransientTimeFrameValidationSchema} from 'pages/comp-pass/comp-pass-validation-schemas';
import {selectExtendedCompPassesEnabled} from 'pages/comp-pass/store/comp-pass-selectors';
import {
    type TransientTimeFrameDraft,
    type TransientTimeFrameErrors,
    type TransientTimeFrameFieldErrors,
} from 'pages/comp-pass/components/comp-pass-wizard/comp-pass-wizard.types';

/**
 * Re-keys the index-keyed error map produced by `transformYupErrorsIntoObject`
 * (the schema validates an array, so paths look like `[0].label`) into a map
 * keyed by row id, which is how the form and slice look errors up.
 */
const mapIndexedErrorsToRowIds = (
    indexedErrors: Record<string, TransientTimeFrameFieldErrors>,
    rows: TransientTimeFrameDraft[]
): TransientTimeFrameErrors => {
    const byRowId = Object.entries(indexedErrors).reduce<
        Record<string, TransientTimeFrameFieldErrors>
    >((acc, [index, fieldErrors]) => {
        const row = rows[Number(index)];

        if (row) {
            acc[row.id] = fieldErrors;
        }

        return acc;
    }, {});

    return Object.keys(byRowId).length > 0 ? byRowId : null;
};

/**
 * Action to add a new (empty) transient time-frame row to the creation flow.
 */
export const addTransientTimeFrame = createAction<TransientTimeFrameDraft>(
    'compPass/addTransientTimeFrame'
);

interface UpdateTransientTimeFramePayload {
    id: string;
    changes: Partial<Omit<TransientTimeFrameDraft, 'id'>>;
}

/**
 * Action to update one or more fields on an existing transient time-frame row.
 */
export const updateTransientTimeFrame =
    createAction<UpdateTransientTimeFramePayload>(
        'compPass/updateTransientTimeFrame'
    );

/**
 * Action to remove a transient time-frame row by id.
 */
export const removeTransientTimeFrame = createAction<string>(
    'compPass/removeTransientTimeFrame'
);

/**
 * Action to reveal validation errors on every transient field regardless of
 * whether it's been filled in. Dispatched when the user tries to advance so the
 * gate can surface required errors on empty fields.
 */
export const forceTransientErrors = createAction(
    'compPass/forceTransientErrors'
);

/**
 * Thunk to validate the current set of transient time-frame rows. Replaces the
 * collection in the slice on both fulfilled and rejected paths so the user can
 * keep editing; rejected additionally surfaces a per-row, per-field error map
 * via `transformYupErrorsIntoObject`.
 *
 * Action Type: compPass/updateAndValidateTransientTimeFrames
 */
export const updateAndValidateTransientTimeFrames = createAsyncThunk<
    TransientTimeFrameDraft[],
    TransientTimeFrameDraft[],
    {
        state: RootState;
        rejectValue: {
            rows: TransientTimeFrameDraft[];
            errors: TransientTimeFrameErrors;
        };
    }
>(
    'compPass/updateAndValidateTransientTimeFrames',
    async (rows, {getState, rejectWithValue}) => {
        const extendedDurationEnabled =
            selectExtendedCompPassesEnabled(getState());
        const validationSchema = getTransientTimeFrameValidationSchema(
            extendedDurationEnabled
        );

        try {
            await validationSchema.validate(rows, {
                abortEarly: false,
            });

            return rows;
        } catch (error) {
            if (error.name === 'ValidationError') {
                const indexedErrors = transformYupErrorsIntoObject(
                    error
                ) as Record<string, TransientTimeFrameFieldErrors>;
                const errors = mapIndexedErrorsToRowIds(indexedErrors, rows);

                return rejectWithValue({rows, errors});
            }

            return rejectWithValue({rows, errors: null});
        }
    }
);
